import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getProfile, switchOrganization } from '../api/api';
import { resolveEffectiveRole, roleHomeRoute, roleLabel } from '../config/roleAccess';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('ems_user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      localStorage.removeItem('ems_user');
      return null;
    }
  });
  const [token, setToken]     = useState(() => localStorage.getItem('ems_token'));
  const [loading, setLoading] = useState(true);

  // ── Org state ──────────────────────────────────────────────────────────────
  const [organizations, setOrganizations] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ems_orgs') || '[]'); } catch { return []; }
  });
  const [activeOrg, setActiveOrg] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ems_active_org') || 'null'); } catch { return null; }
  });
  const [orgRole, setOrgRole] = useState(() => localStorage.getItem('ems_org_role') || null);
  const [permissions, setPermissions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ems_permissions') || '[]'); } catch { return []; }
  });
  const [permissionSources, setPermissionSources] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ems_permission_sources') || '{}'); } catch { return {}; }
  });

  const _persistOrgs = (orgs, active, role) => {
    setOrganizations(orgs || []);
    setActiveOrg(active || null);
    setOrgRole(role || null);
    localStorage.setItem('ems_orgs', JSON.stringify(orgs || []));
    localStorage.setItem('ems_active_org', JSON.stringify(active || null));
    if (role) localStorage.setItem('ems_org_role', role);
    else localStorage.removeItem('ems_org_role');
  };

  const logout = useCallback(() => {
    localStorage.removeItem('ems_token');
    localStorage.removeItem('ems_user');
    localStorage.removeItem('ems_orgs');
    localStorage.removeItem('ems_active_org');
    localStorage.removeItem('ems_org_role');
    localStorage.removeItem('ems_permissions');
    localStorage.removeItem('ems_permission_sources');
    setToken(null);
    setUser(null);
    setOrganizations([]);
    setActiveOrg(null);
    setOrgRole(null);
    setPermissions([]);
    setLoading(false);
  }, []);

  const loadProfile = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      const res = await getProfile();
      const profile = res?.data?.data || null;
      setUser(profile);
      if (profile) {
        localStorage.setItem('ems_user', JSON.stringify(profile));
        if (profile.organizations !== undefined) {
          _persistOrgs(profile.organizations, profile.active_org, profile.org_role);
        }
        const nextPermissions = Array.isArray(profile.permissions) ? profile.permissions : [];
        localStorage.setItem('ems_permissions', JSON.stringify(nextPermissions));
        setPermissions(nextPermissions);
        const nextPermissionSources = profile?.permission_sources && typeof profile.permission_sources === 'object'
          ? profile.permission_sources
          : {};
        localStorage.setItem('ems_permission_sources', JSON.stringify(nextPermissionSources));
        setPermissionSources(nextPermissionSources);
      } else {
        localStorage.removeItem('ems_user');
      }
    } catch {
      if (!silent) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) { loadProfile(); }
    else { setUser(null); setLoading(false); }
  }, [token, loadProfile]);

  useEffect(() => {
    if (!token) return;

    const syncProfileSilently = () => {
      loadProfile({ silent: true });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncProfileSilently();
      }
    };

    const intervalId = setInterval(syncProfileSilently, 60000);
    window.addEventListener('focus', syncProfileSilently);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', syncProfileSilently);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [token, loadProfile]);

  useEffect(() => {
    const handle = () => logout();
    window.addEventListener('ems:unauthorized', handle);
    return () => window.removeEventListener('ems:unauthorized', handle);
  }, [logout]);

  const login = (tokenVal, userData) => {
    localStorage.setItem('ems_token', tokenVal);
    localStorage.setItem('ems_user', JSON.stringify(userData));
    setToken(tokenVal);
    setUser(userData);
    if (userData?.organizations !== undefined) {
      _persistOrgs(userData.organizations, userData.active_org, userData.org_role);
    }
    const nextPermissions = Array.isArray(userData?.permissions) ? userData.permissions : [];
    localStorage.setItem('ems_permissions', JSON.stringify(nextPermissions));
    setPermissions(nextPermissions);
    const nextPermissionSources = userData?.permission_sources && typeof userData.permission_sources === 'object'
      ? userData.permission_sources
      : {};
    localStorage.setItem('ems_permission_sources', JSON.stringify(nextPermissionSources));
    setPermissionSources(nextPermissionSources);
    setLoading(false);
  };

  const switchOrg = async (orgId) => {
    const res = await switchOrganization({ org_id: orgId });
    const d = res?.data?.data;
    if (d?.token) { localStorage.setItem('ems_token', d.token); setToken(d.token); }
    if (d?.active_org) _persistOrgs(organizations, d.active_org, d.org_role);
    if (Array.isArray(d?.permissions)) {
      localStorage.setItem('ems_permissions', JSON.stringify(d.permissions));
      setPermissions(d.permissions);
    }
    if (d?.permission_sources && typeof d.permission_sources === 'object') {
      localStorage.setItem('ems_permission_sources', JSON.stringify(d.permission_sources));
      setPermissionSources(d.permission_sources);
    }
    return d;
  };

  const isSuperAdmin      = user?.role === 'super_admin';
  const isPlatformAdmin   = user?.role === 'admin';
  const effectiveRole = resolveEffectiveRole(user?.role, orgRole);
  const effectiveRoleLabel = roleLabel(effectiveRole);
  const defaultHomeRoute = roleHomeRoute(effectiveRole);
  const isOrganizationAdminRole = ['organization_head', 'admin'].includes(orgRole);
  const isAdmin           = user?.role === 'admin' || isOrganizationAdminRole || isSuperAdmin;
  const isOrgHead         = orgRole === 'organization_head';
  const isOrgAdmin        = orgRole === 'admin' || isOrgHead;
  const hasPermission = (permissionCode) => permissions.includes(permissionCode);
  const hasAnyPermission = (permissionCodes = []) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return true;
    return permissionCodes.some((permissionCode) => permissions.includes(permissionCode));
  };
  const hasAllPermissions = (permissionCodes = []) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return true;
    return permissionCodes.every((permissionCode) => permissions.includes(permissionCode));
  };
  const hasAnyRole = (allowedRoles = []) => {
    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
    return allowedRoles.includes(effectiveRole);
  };
  const canViewDashboardCards = isSuperAdmin || hasPermission('view_dashboard_cards');
  const canViewAnalytics = isSuperAdmin || hasAnyPermission(['view_analytics', 'analytics.organization.view']);
  const canViewProjects = isSuperAdmin || hasAnyPermission(['view_projects', 'projects.view']);
  const canCreateProjects = isSuperAdmin || hasAnyPermission(['create_projects', 'projects.manage']);
  const canEditProjects = isSuperAdmin || hasAnyPermission(['edit_projects', 'projects.manage']);
  const canDeleteProjects = isSuperAdmin || hasAnyPermission(['delete_projects', 'projects.manage']);
  const canManageProjects = canCreateProjects || canEditProjects || canDeleteProjects;
  const canViewTransactions = isSuperAdmin || hasAnyPermission(['view_transactions', 'transactions.view']);
  const canCreateTransactions = isSuperAdmin || hasAnyPermission(['create_transactions', 'transactions.manage']);
  const canEditTransactions = isSuperAdmin || hasAnyPermission(['edit_transactions', 'transactions.manage']);
  const canDeleteTransactions = isSuperAdmin || hasAnyPermission(['delete_transactions', 'transactions.manage']);
  const canExportTransactions = isSuperAdmin || hasPermission('export_transactions');
  const canViewReports = isSuperAdmin || hasPermission('view_reports');
  const canExportReports = isSuperAdmin || hasPermission('export_reports');
  const canViewUsers = isSuperAdmin || hasPermission('view_users');
  const canInviteUsers = isSuperAdmin || hasPermission('invite_users');
  const canEditUsers = isSuperAdmin || hasPermission('edit_users');
  const canSuspendUsers = isSuperAdmin || hasPermission('suspend_users');
  const canManageCategories = isSuperAdmin || hasPermission('manage_categories');
  const canManagePersons = isSuperAdmin || hasPermission('manage_persons');
  const canManageOrganizationSettings = isSuperAdmin || hasPermission('manage_organization_settings');
  const canViewAuditLogs = isSuperAdmin || hasPermission('view_audit_logs');
  const canManageRolePermissions = isSuperAdmin || hasPermission('manage_role_permissions');
  const canManageMembers  = isOrgHead || (isOrgAdmin && hasAnyPermission(['view_users', 'invite_users', 'edit_users', 'suspend_users']));
  const canManageFinance  = ['organization_head', 'admin', 'finance_manager'].includes(orgRole) || hasAnyPermission(['view_analytics', 'view_reports', 'export_reports']);
  const canApproveExpense = ['organization_head', 'admin', 'finance_manager'].includes(orgRole);
  const canViewProjectHistory =
    isSuperAdmin
    || ['organization_head', 'admin', 'project_manager', 'finance_manager'].includes(orgRole)
    || hasPermission('view_project_history')
    || hasPermission('analytics.organization.view');
  const permissionSource = (permissionCode) => permissionSources?.[permissionCode] || 'System Default Permission';

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isAuthenticated: !!token && !!user,
      login, logout, refreshProfile: loadProfile,
      isAdmin, isSuperAdmin, isPlatformAdmin,
      effectiveRole, effectiveRoleLabel, defaultHomeRoute,
      organizations, activeOrg, orgRole,
      permissions, permissionSources, permissionSource, hasPermission, hasAnyPermission, hasAllPermissions,
      hasAnyRole,
      canViewDashboardCards, canViewAnalytics,
      canViewProjects, canCreateProjects, canEditProjects, canDeleteProjects,
      canManageProjects, canViewProjectHistory,
      canViewTransactions, canCreateTransactions, canEditTransactions, canDeleteTransactions, canExportTransactions,
      canViewReports, canExportReports,
      canViewUsers, canInviteUsers, canEditUsers, canSuspendUsers,
      canManageCategories, canManagePersons,
      canManageOrganizationSettings, canViewAuditLogs, canManageRolePermissions,
      switchOrg, isOrgHead, isOrgAdmin,
      canManageMembers, canManageFinance, canApproveExpense,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
